import type { FormikProps } from 'formik'
import React from 'react'

import { Box, Flex, Stack, Tag, Text } from '@masterplandev/ui'

import useTranslation from '@/core/hooks/useTranslation'
import { PollSection } from '@/http/api/polls'

import { PollControl } from '../PollControls/PollControl'
import { PollLabel } from '../PollControls/PollLabel'
import { PollSlider } from '../PollControls/PollSlider'
import { PollTextarea } from '../PollControls/PollTextarea'

type PollFormStepProps = {
  pollSection?: PollSection
  form: FormikProps<object>
  onValidate: (isValid: boolean) => void
}

export function PollFormStep({
  pollSection,
  form,
  onValidate,
}: PollFormStepProps) {
  const { translate } = useTranslation('poll-form-step')

  const questions = pollSection?.questions?.filter(
    (question) => question.type === 'rate' || question.type === 'free_text',
  )

  const questionsCount = questions?.length
  const answersCount = questions?.filter(
    (question) => form.values[question.id],
  ).length

  const isValid = answersCount === questionsCount

  React.useEffect(() => {
    onValidate(isValid)
  }, [isValid, onValidate])

  if (!pollSection) {
    return null
  }

  return (
    <Box>
      <Flex
        align="flex-start"
        justify="space-between"
        alignItems="center"
        gap="8"
        w="100%"
        py="6">
        <Text fontSize="15px">{pollSection.title}</Text>
        <Tag
          size="lg"
          bg="mp2.neutralDark.300A"
          colorScheme="white"
          flexShrink={0}
          variant="faded">
          {answersCount}/{questionsCount} {translate('answered')}
        </Tag>
      </Flex>
      <Stack gap="4" maxW="900px" margin="auto">
        {questions?.map((question) => (
          <PollControl key={question.id} isSelected={form.values[question.id]}>
            <PollLabel htmlFor={question.id}>{question.text}</PollLabel>
            {question.type === 'rate' && (
              <PollSlider
                minLabel={question.min_label || '-'}
                maxLabel={question.max_label || '-'}
                min={question.min_value || 1}
                max={question.max_value || 5}
                id={question.id}
                name={question.id}
                defaultValue={form.values[question.id] || 3}
                onChange={(value) => {
                  form.setFieldValue(question.id, value)
                }}
              />
            )}
            {question.type === 'free_text' && (
              <PollTextarea
                name={question.id}
                defaultValue={form.values[question.id]}
                onChange={(event) => {
                  form.setFieldValue(question.id, event.target.value)
                }}
              />
            )}
          </PollControl>
        ))}
      </Stack>
    </Box>
  )
}
